import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import MasterListPage from "./ServiceMasterList";
import ActionDropdown from "Common/ActionDropdown/ActionDropdown";
// import { fetchSalesOrders, exportSalesOrdersCSV } from "slices/salesorder/thunk";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { formatIndianAmount } from "helpers/formatIndianAmount";
import { showConfirm } from "helpers/swalHelper";
import { useDispatch } from "react-redux";
import { handleRevertServiceSalesOrderStatus } from "slices/sales_order/thunk";
import { toast } from "react-toastify";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Tooltip from "@mui/material/Tooltip";
import Chip from "components/Chips/Chip";

interface SalesOrderPageProps {
  type?: "service"; // Service specific, defaults to service
}

const SalesOrderDraft: React.FC<SalesOrderPageProps> = ({ type = "service" }) => {
  const { tab } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [reloadFlag, setReloadFlag] = useState(0);
  document.title = "Service Order";
  // Get permissions for service sales orders
  const { canView, canUpdate } = usePermission(
    app_modules?.sales_order?.app_name,
    app_modules?.sales_order?.modules?.service
  );

  const activeType = "service"; // Hardcoded for service
  const activeTab = tab;

  // dynamic title
  const title =
    activeTab === "sales-order-log"
      ? "Order Log"
      : activeTab === "junk"
      ? "Junk"
      : "Draft";

  // dynamic add label
  const addButtonLabel = "Create Service Order";

  // dynamic add route
  const addRoute = `/sales_order/add_services_sales_order`;

  const handleView = (salesOrderNo: string) => {
    // redirect to single sales order page
    navigate(`/sales_order/${activeType}/${activeTab}/view/${salesOrderNo}`);
  };

  const handleEdit = (salesOrderNo: string) => {
    // redirect to edit form
    navigate(`/sales_order/${activeType}/edit/${salesOrderNo}`, {
      state: { tab: activeTab }
    });
  };

  const handleDelete = async (salesOrderNo: string) => {
    const result = await showConfirm(
      "Are you sure you want to delete this sales order?",
      "Delete Sales Order",
      "Yes, Delete",
      "Cancel"
    );
    
    if (result.isConfirmed) {
      try {
        await dispatch(handleRevertServiceSalesOrderStatus(salesOrderNo)).unwrap();
        toast.success("Sales order deleted successfully");
        // Trigger refresh by incrementing reloadFlag
        setReloadFlag(prev => prev + 1);
        // Refresh is now handled by refreshTrigger in ServiceMasterList
      } catch (error: any) {
        toast.error(error?.message || "Failed to delete sales order");
      }
    }
  };

  const handleRestore = (salesOrderNo: string) => {
    // dispatch(restoreSalesOrder(salesOrderNo))
  };

  const handleFollowUp = (salesOrderNo: string) => {
    // redirect to follow up form
    navigate(`/sales_order/followups/${activeType}/${salesOrderNo}`);
  };
  const handleCreateSalesOrder = (salesOrderNo: string) => {
    // redirect to create sales order form
    navigate(`/sales-order/${activeType}/create-from-enquiry/${salesOrderNo}`);
  };

  const getColumns = () => {
    const commonColumns = [
      // {
      //   header: "Sr. No.",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row, table }: { row: any; table: any }) =>
      //     table.getState().pagination.pageIndex *
      //       table.getState().pagination.pageSize +
      //     row.index +
      //     1,
      // },
      {
        header: "SRV No.",
        accessorKey: "sales_order_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.SERVICE.FILTER_DROPDOWN("sales_order_number")}&stage=draft`,
        cell: ({ row }: { row: any }) =>
          row.original.sales_order_number || "-",
      },
      {
        header: "Quotation Reference",
        accessorKey: "quotation_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.SERVICE.FILTER_DROPDOWN("quotation_number")}&stage=draft`,
        cell: ({ row }: { row: any }) =>
          row.original.quotation_number || "-",
      },

      {
        header: "Service Number",
        accessorKey: "service_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.SERVICE.FILTER_DROPDOWN("service_number")}&stage=draft`,
        cell: ({ row }: { row: any }) => row.original.service_number || "-",
      },
      {
        header: "Customer Name",
        accessorKey: "company_name",
        enableColumnFilter: true,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.SERVICE.FILTER_DROPDOWN("company_name")}&stage=draft`,
        cell: ({ row }: { row: any }) =>
          row.original.customer_details?.company_name || "-",
      },
      {
        header: "Organisation",
        accessorKey: "bill_from",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => row.original?.bill_from?.organization_name || "-",
      },
      {
        header: "SRF Date",
        accessorKey: "srv_date",
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "date" as const,
        filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.SERVICE.FILTER_DROPDOWN("srv_date")}&stage=draft`,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.srv_date} />
        ),
      },
      {
        header: "Agent",
        accessorKey: "agent_name",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.SERVICE.FILTER_DROPDOWN("agent_name")}&stage=draft`,
        cell: ({ row }: { row: any }) =>
          row.original.agent_name || "-",
      },

      {
        header: "Grand  Total Amount",
        accessorKey: "grand_total_amount",
        enableColumnFilter: false,
        enableSorting: true,
        filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.SERVICE.FILTER_DROPDOWN("grand_total_amount")}&stage=draft`,
        cell: ({ row }: { row: any }) => {
          const val = row.original.grand_total_amount;
          const sym = row.original.currency_symbol || "₹";
          return val != null && val !== "" ? `${sym} ${formatIndianAmount(val)}` : "-";
        },
      },
      {
        header: "Assigned To",
        accessorKey: "assigned_members",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.PRODUCT.FILTER_DROPDOWN("assigned_members")}&stage=draft`,

        cell: ({ row }: { row: any }) => {
          const members = row.original?.assigned_members || [];

          if (!members.length) return "-";

          const names = members.map(
            (m: any) =>
              `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.username
          );

          const visible = names.slice(0, 4);
          const remaining = names.length - visible.length;

          return (
            <Tooltip
              arrow
              placement="top"
              title={names.join("\n")}
              componentsProps={{
                tooltip: {
                  sx: {
                    whiteSpace: "pre-line",
                    maxWidth: 250,
                    fontSize: "13px",
                  },
                },
              }}
            >
              <div style={{ cursor: "pointer", lineHeight: 1.4 }}>
                {visible.map((name: string, index: number) => (
                  <div key={index}>{name}</div>
                ))}

                {remaining > 0 && (
                  <div style={{ fontSize: "12px", color: "#6c757d" }}>
                    +{remaining} more
                  </div>
                )}
              </div>
            </Tooltip>
          );
        },
      },
      // {
      //   header: "Status",
      //   accessorKey: "sales_order_status",
      //   id: "status",
      //   enableColumnFilter: true,
      //   enableSorting: false,
      //   filterType: "dropdown" as const,
      //   filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.SERVICE.FILTER_DROPDOWN("sales_order_status")}&stage=draft`,
      //   cell: ({ row }: { row: any }) => {
      //     return row.original.sales_order_status || "-";
      //   },
      //   // cell: ({ row }: { row: any }) => {
      //   //   return <StatusDropdown currentStatus={row.original.sales_order_status} itemId={row.original.sales_order_id} onStatusChange={handleStatusChange} statusOptions={salesOrderStatusOptions} disabled={false} />
      //   // }
      // },
      // {
      //   header: "PGB Status",
      //   accessorKey: "pgb_status",
      //   id: "pgb_status",
      //   enableColumnFilter: true,
      //   enableSorting: false,
      //   filterType: "dropdown" as const,
      //   filterFetchUrl: `/sales_order/${activeType}/dynamic_dropdown/status?stage=draft`,
      // },

      {
        header: "Assigned Technician",
        accessorKey: "assigned_technician",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.SALES_ORDER.PRODUCT.FILTER_DROPDOWN("assigned_technician")}&stage=draft`,

        cell: ({ row }: { row: any }) => {
          const technicians = row.original?.assigned_technician || [];

          if (!technicians.length) return "-";

          const names = technicians.map(
            (m: any) =>
              `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.username
          );

          const visible = names.slice(0, 4);
          const remaining = names.length - visible.length;

          return (
            <Tooltip
              arrow
              placement="top"
              title={names.join("\n")}
              componentsProps={{
                tooltip: {
                  sx: {
                    whiteSpace: "pre-line",
                    maxWidth: 250,
                    fontSize: "13px",
                  },
                },
              }}
            >
              <div style={{ cursor: "pointer", lineHeight: 1.4 }}>
                {visible.map((name: string, index: number) => (
                  <div key={index}>{name}</div>
                ))}

                {remaining > 0 && (
                  <div style={{ fontSize: "12px", color: "#6c757d" }}>
                    +{remaining} more
                  </div>
                )}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Notes",
        accessorKey: "notes",
        enableColumnFilter: false,
        enableSorting: false,
        minSize: 200,
        size: 200,
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              maxWidth: "200px",
              minWidth: "200px",
            }}
          >
            {row.original.notes || "-"}
          </div>
        ),
        // filterType: "dropdown" as const,
        // filterFetchUrl: `/sales_order/${activeType}/dynamic_dropdown/notes?stage=draft`,
      },
       {
              header: "Tags",
              accessorKey: "tags",
              enableColumnFilter: false,
              enableSorting: false,
              cell: ({ row }: { row: any }) => {
                const tags: string[] = row.original.tags || [];
                if (!tags.length) return "-";
      
                const maxVisible = 3;
                const visible = tags.slice(0, maxVisible);
                const remaining = tags.length - visible.length;
                const tooltipText = tags.join(", ");
      
                return (
                  <Tooltip
                    arrow
                    placement="top"
                    title={tooltipText}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 300,
                          fontSize: "13px",
                          whiteSpace: "normal",
                        },
                      },
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "4px",
                        minWidth: "150px",
                        cursor: "pointer",
                      }}
                    >
                      {visible.map((tag: string) => (
                        <Chip key={tag}>{tag}</Chip>
                      ))}
                      {remaining > 0 && <Chip>+{remaining} more</Chip>}
                    </div>
                  </Tooltip>
                );
              },
            },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.created_by_user;
          const displayName = user
            ? (typeof user === 'string'
                ? user
                : user.username || '-')
            : '-';
          return (
            <NameWithDate
              name={displayName}
              date={row.original.created_at}
            />
          );
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.updated_by_user;
          const displayName = user
            ? (typeof user === 'string'
                ? user
                :  user.username || '-')
            : '-';
          return (
            <NameWithDate
              name={displayName}
              date={row.original.updated_at}
            />
          );
        },
      },
    ];

    const actionColumn = {
      header: "Action",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const sales_order_id = row.original.sales_order_service_id || row.original.id;
        // console.log("row.original in line 223", row.original)
        // console.log("sales_order_id in line 224", sales_order_id)
        const actions = [
          ...(canView ? [{
            label: "View",
            permission: canView,
            icon: <VisibilityIcon fontSize="small" color="info" />,
            onClick: () => handleView(sales_order_id),
          }] : []),
          ...(canUpdate ? [{
            label: "Edit",
            permission: canUpdate,
            icon: <EditIcon fontSize="small" color="primary" />,
            onClick: () => handleEdit(sales_order_id),
          }] : []),
          ...(canUpdate ? [{
            label: "Delete",
            permission: canUpdate,
            icon: <DeleteOutlineIcon fontSize="small" color="error" />,
            onClick: () => handleDelete(sales_order_id),
            className: "text-danger",
          }] : []),
        ];

        // return <ActionDropdown actions={actions} />;
        return <DynamicMenuItem row={row.original} menuItems={actions} />;
      },
    };
    return [...commonColumns, actionColumn];
  };

  return (
    <>
      <MasterListPage
        title={`Service ${title}`}
        addButtonLabel={addButtonLabel}
        addRoute={addRoute}
        fetchThunk={"fetchSalesOrders"}
        exportThunk={"exportSalesOrdersCSV"}
        columns={getColumns()}
        statusToggleUrl={`/sales_order/service`}
        activeTab={activeTab}
        activeType={"service"}
        reloadFlag={reloadFlag}
      />
    </>
  );
};

export default SalesOrderDraft;
